import { localStorageGet, localStorageSet } from 'app/shared/utils/window.util';
import { assertPositive } from 'app/utils/assertUtils';
import { shellStringResources } from 'shell-models-lib';
export const azureLanguageList = ['en', 'cs', 'de', 'es', 'fr', 'hu', 'id', 'it', 'ja', 'ko', 'nl', 'pl', 'pt-BR', 'pt-PT', 'ru', 'sv', 'tr', 'zh-Hans', 'zh-Hant'];
export const office365LanguageList = azureLanguageList.concat(['ar', 'bg', 'ca', 'da', 'el', 'et', 'eu', 'fi', 'gl', 'he', 'hi', 'hr', 'kk', 'lt', 'lv', 'ms', 'no', 'ro', 'sk', 'sl', 'sr-Cyrl-RS', 'sr-Latn-RS', 'th', 'uk', 'vi']);

const localeIds = office365LanguageList; //  Using trident language set
let hasAppliedTransparentMigrationRebrand: boolean = false;

// List of known branded strings that will be updated during localization loading process
const transparentMigrationRebrandStringKeys: readonly string[] = [
    'ADFCDC',
    'ADFHomeCommunityContentText',
    'ADFHomeTutorialsText',
    'ADFHomeVideosText',
    'ADFOneClickSetUp',
    'ADF_Workspace_SelectMethodDescription',
    'ADF_Workspace_SelectionMethod',
    'ARMManagementDesc',
    'Adf',
    'AdfBanner',
    'AirflowBanner',
    'Authoring_Home_MountedDataFactory_Prompt',
    'AzDevOpsProjectNotFoundError',
    'AzureDataFactory',
    'BillingOperationsFooter',
    'ConfirmAdfSwitch',
    'CopyActivity_FaultTolerance_SkipForbiddenFilesDescription',
    'CreateSubscriptionDesc',
    'CustomerManagedKeyDescription',
    'CustomerManagedKeyNotAvailable',
    'DataFlowVersion_LiveTooltip',
    'DisableDataFlowPreviewBanner',
    'EnablePreviewFeaturesDescription',
    'ExecutePipelineADFConnectionTooltip',
    'ExportArmTemplateDesc',
    'Fabric_About_Description',
    'Fabric_About_Monitoring_Description',
    'Fabric_About_Platform_Description',
    'Fabric_Connection',
    'Fabric_DataFactory_Header',
    'Fabric_DataFactory_Label',
    'Fabric_Workspace_Description',
    'Fabric_Workspace_Panel_Title',
    'FeatureShowcaseAutomatedPublishDescription',
    'FeatureShowcaseCDMandDeltaLakeDescription',
    'FeatureShowcasePowerQueryDescription',
    'FeatureShowcasePurviewTitle',
    'FeatureShowcaseSAPTemplateAreaDescription',
    'FollowADF',
    'GitDisablePublish',
    'GitDisablePublishTooltip',
    'GitHubByoaDescription',
    'GitHubTokenInvalidByoa',
    'GitPublishDisabledTooltip',
    'GitPublishLabel',
    'GitRootFolderDescription',
    'IRError_SSIS_ExpressCustomSetupFailure_Popup',
    'IRError_SSIS_JoinVNetPermissionNotGranted',
    'ImportArmTemplateDesc',
    'InvokeADFPipelineActivityLabel',
    'InvokePipelineActivityPipelineReturnValueBanner',
    'ManagementHub_Confirm_Message',
    'Migration_PopupContent',
    'Migration_PopupInfo',
    'Migration_SelectConnectionsDescription',
    'MoreIsPossible',
    'MountInProgressTitle',
    'MountSucceedTitle',
    'MountWorkspaceIsMounted_Description',
    'NewDataFlowView',
    'OverviewSurveyTitle',
    'OverviewVideo',
    'OverviewVideoADFGit',
    'OverviewVideoCodeFree',
    'OverviewVideoContinuousIntegration',
    'OverviewVideoCreateDependent',
    'OverviewVideoDataQuality',
    'OverviewVideoEnhancedProductivity',
    'OverviewVideoEventBased',
    'OverviewVideoGanttViews',
    'OverviewVideoIntegrateWithDatabricks',
    'OverviewVideoIterativeDevelopment',
    'OverviewVideoProactiveAlerts',
    'OverviewVideoRerunActivities',
    'OverviewVideoRunAzureFunctions',
    'OverviewVideoSCDPatterns',
    'OverviewVideoTransformData',
    'OverviewVideoVisuallyBuildPipelines',
    'SAP_Template_Banner_Desc',
    'SaveTemplateRequireGitRepository_Message',
    'SelectFactoryDescription',
    'SelectFactoryNotAccessible',
    'ServiceUpdatesManagedSSIS',
    'ServiceUpdatesNewCapabilities',
    'ServiceUpdatesToolsEnabled',
    'SetupCodeRepositoryDescription',
    'TemplateCreationFailureADFDesc',
    'TryDataFirstView',
    'UnmountInProgressTitle',
    'UploadTridentTemplateError',
    'UseAdfMode',
    'WelcomeToDataFactory',
    'WelcometoADF',
    'npsQuestion',
    'FDFMarketingBanner_MountedDf'
];

function isClassicRebrandEnabled(): boolean {
    const featureFdfClassicValue = new URLSearchParams(window.location.search).get('feature.fdfClassic');
    return featureFdfClassicValue?.toLowerCase() === 'true' && !__IS_A365__;
}

function applyTransparentMigrationRebrand(): void {
    if (!isClassicRebrandEnabled() || hasAppliedTransparentMigrationRebrand) {
        return;
    }

    if (!window.stringResources || typeof window.stringResources !== 'object') {
        return;
    }

    const stringResourcesRecord = window.stringResources as unknown as Record<string, unknown>;
    const replacementName = window.stringResources?.RebrandDataFactory || 'Data Factory (Classic)';
    const rebrandPattern = /azure\s+data\s+factory|\bADF\b/gi;

    for (const key of transparentMigrationRebrandStringKeys) {
        const value = stringResourcesRecord[key];
        if (typeof value === 'string') {
            stringResourcesRecord[key] = value.replace(rebrandPattern, replacementName);
        }
    }
    hasAppliedTransparentMigrationRebrand = true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StaticLocaleRecordType = Record<string | number, any>;
export type StaticLocaleRecordBuilder<T> = () => T;

/**
 * A tuple array of [localeString, targetObject, targetObjectBuilder]
 *      localeString: the locale string when registering the targetObjectBuilder
 *      targetObject: the target static reference of locale string
 *      targetObjectBuilder: the builder of the target static record object
 */
const pendingBuilderArray: Array<[string, StaticLocaleRecordType, StaticLocaleRecordBuilder<StaticLocaleRecordType>]> = [];

/**
 * In-place copy internal primitive references in an array from source to target
 * @param source Array
 * @param target Array
 */
function inPlaceWriteFromSourceToTargetArray(source: Array<StaticLocaleRecordType | unknown>, target: Array<StaticLocaleRecordType | unknown>) {
    assertPositive(
        source.length === target.length,
        'Source and target array should be of the same size when we do in-place copying'
    );
    target.forEach((value, index) => {
        // for scalar primitive types, directly do copy
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            target[index] = source[index];
        } else if (Array.isArray(value)) {
            assertPositive(
                Array.isArray(source[index]),
                'Source and target array item should both be of the array type'
            );
            inPlaceWriteFromSourceToTargetArray(source[index] as Array<StaticLocaleRecordType | unknown>, target[index] as Array<StaticLocaleRecordType | unknown>);
        } else if (typeof value === 'object') {
            inPlaceWriteFromSourceToTargetRecord(source[index], target[index]);
        } else {
            assertPositive(
                false,
                'We should not have a non string/number/array/object array item among a static record of locale strings.'
            );
        }
    });
}

/**
 * In-place copy internal primitive references in a record object from source to target
 * @param source Record object
 * @param target Record object
 * @param initializing when we in-place initialize a const record, we would directly copy its first level fields
 */
function inPlaceWriteFromSourceToTargetRecord(source: StaticLocaleRecordType, target: StaticLocaleRecordType, initializing: boolean = false) {
    for (const objectKey in source) {
        if (source.hasOwnProperty(objectKey)) {
            // for scalar primitive types, directly do copy
            if (typeof source[objectKey] === 'string' || typeof source[objectKey] === 'number' || typeof source[objectKey] === 'boolean') {
                target[objectKey] = source[objectKey];
            } else if (Array.isArray(source[objectKey])) {
                if (initializing) {
                    target[objectKey] = source[objectKey];
                } else {
                    inPlaceWriteFromSourceToTargetArray(source[objectKey], target[objectKey]);
                }
            } else if (typeof source[objectKey] === 'object') {
                if (initializing) {
                    target[objectKey] = source[objectKey];
                } else {
                    inPlaceWriteFromSourceToTargetRecord(source[objectKey], target[objectKey]);
                }
            } else {
                assertPositive(
                    false,
                    'We should not have a non string/number/array/object field among a static record of locale strings.'
                );
            }
        }
    }
}

/**
 *  A helper class which would register a static record of locale string
 *  whose primitive types would be updated synchronously once a new locale set got settled.
 */
export class StaticLocaleRecord<T extends StaticLocaleRecordType> {

    private readonly _value: T;

    /**
     * @param builderFunction the builder function returning a new record of local strings
     * @param initValue an optional blank static record of locale strings
     */
    constructor(
        builderFunction: StaticLocaleRecordBuilder<T>,
        initValue: T = ({} as unknown) as T
    ) {

        this._value = initValue;

        assertPositive(!!this._value && typeof this._value === 'object',
            'StaticLocaleRecord param has to be an nonNullable record in registerLocaleBuilder');

        const newObject = builderFunction() as Record<string, string>;
        // merely directly assign the first level fields while initializing
        inPlaceWriteFromSourceToTargetRecord(newObject, this._value, true);
        pendingBuilderArray.push([window.__locale, this._value, builderFunction]);
    }

    get value(): T {
        return this._value;
    }
}

/**
 * Synchronously clear all previously registered locale records if registered in a different language.
 * @private
 */
function clearStaticBuilder() {
    for (const [localeWhileRegistering, targetObject, theBuilder] of pendingBuilderArray) {
        if (localeWhileRegistering !== window.__locale) {
            const newObject = theBuilder() as Record<string, string>;
            inPlaceWriteFromSourceToTargetRecord(newObject, targetObject as Record<string | number, string>);
        }
    }
    // clean all registered locale builders
    pendingBuilderArray.length = 0;
}

export function loadLocalization(): Promise<void> {
    return new Promise((resolve) => {
        const initialHref = document.head.getElementsByTagName('base')[0].href;
        let initialUrl = window.location.href.substr(initialHref.length);
        const urlParts = initialUrl.split('/');
        let localePart = urlParts.length > 0 ? urlParts[0] : undefined;
        let localeId = localePart !== undefined ? localePart.toLowerCase() : undefined;
        let localeIdInUrl = localeIds.find((candidate) => candidate.toLowerCase() === localeId);

        if (localeIdInUrl == null) {
            if (localeId === 'en-us') { // update 'en-us' to 'en'
                localeIdInUrl = localeId = localePart = 'en';
                initialUrl = initialUrl.substr('en-us/'.length);
            } else {
                const lastUsedLocaleId = localStorageGet('Redirect_LastUsedLocale');
                const validCachedId = lastUsedLocaleId
                    ? localeIds.find((candidate) => candidate.toLowerCase() === lastUsedLocaleId.toLowerCase())
                    : undefined;
                localeIdInUrl = localePart = validCachedId || localeIds[0];
                localeId = localePart.toLowerCase();
            }
            const newUrl = initialHref + localePart + '/' + initialUrl;
            window.history.pushState(undefined, undefined, newUrl); // avoid reload
        }
        // update Redirect_LastUsedLocale with localeIdInUrl
        localStorageSet('Redirect_LastUsedLocale', localeIdInUrl);
        const hrefPrefix = initialHref.endsWith('/') ? '' : '/';
        document.head.getElementsByTagName('base')[0].href = initialHref + hrefPrefix + localePart;

        if (localeId !== localeIds[0]) {
            const formattedLocalId = localeIds.find((i) => i.toLowerCase() === localeId);
            pendingBuilderArray.push([window.__locale, shellStringResources, () => (window.stringResources.shellStringResources)]);
            fetch('_locales/StrResources.' + formattedLocalId + '.json').then((res) => {
                if (res.ok) {
                    res.text().then((text) => {
                        window.stringResources = { ...window.stringResources, ...JSON.parse(text) };
                        applyTransparentMigrationRebrand();
                        window.__locale = formattedLocalId;
                        clearStaticBuilder();
                        resolve();
                    });
                } else {
                    throw new Error('Load locale file failed.');
                }
            }).catch(() => {
                applyTransparentMigrationRebrand();
                resolve();
            });
        } else {
            applyTransparentMigrationRebrand();
            resolve();
        }
    });
}

export function getLocalizationData() {
    const actualLanguage = window.__locale || 'en';
    const expectedLanguage = localStorageGet('Redirect_LastUsedLocale');

    return {
        actualLanguage,
        expectedLanguage,
    };
}
